import React, { useEffect, useMemo, useState, useCallback } from "react";
import { X } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "../Accounting/FixedAssets/fixedAssets.css";

function sum(arr, key) {
  return (arr || []).reduce((s, r) => s + Number(r[key] || 0), 0);
}

export default function MovementDetailsModal({
  movement,
  onClose,
  warehouseName,
}) {
  const { companyId } = useCompany();
  const [lines, setLines] = useState([]);
  const [journals, setJournals] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !movement?.id) return;
    setErr("");

    const { data: l, error: lErr } = await supabase
      .from("inventory_movement_lines")
      .select(
        "id,item_id,quantity,unit_cost,total_cost,batch_no,expiry_date,serials, inventory_items:inventory_items(id,sku,name,unit)"
      )
      .eq("company_id", companyId)
      .eq("movement_id", movement.id)
      .order("created_at", { ascending: true });

    if (lErr) setErr(lErr.message);
    setLines(l || []);

    // Journals drilldown: source_type = inventory_movement, source_id = movement_id
    const { data: j, error: jErr } = await supabase
      .from("journal_entries")
      .select(
        "id,date,description,account_id,money_in,money_out,source_type,source_id,is_reversal"
      )
      .eq("company_id", companyId)
      .eq("source_type", "inventory_movement")
      .eq("source_id", movement.id)
      .order("date", { ascending: true });

    if (jErr) setErr((p) => p || jErr.message);
    setJournals(j || []);
  }, [companyId, movement?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const cost = sum(lines, "total_cost");
    const dr = sum(journals, "money_in");
    const cr = sum(journals, "money_out");
    return { cost, dr, cr };
  }, [lines, journals]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(980px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Movement Details
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              {movement.movement_date} • <b>{movement.movement_type}</b> •{" "}
              {warehouseName?.(movement.warehouse_id)}
              {movement.movement_type === "TRANSFER"
                ? ` → ${warehouseName?.(movement.warehouse_to_id)}`
                : ""}
            </div>
          </div>
          <button className="fa-icon-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}

        <div className="fa-card" style={{ marginTop: 12 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <div className="label">Lines Total Cost</div>
              <div className="value">{totals.cost.toFixed(2)}</div>
            </div>
            <div>
              <div className="label">Journals Dr</div>
              <div className="value">{totals.dr.toFixed(2)}</div>
            </div>
            <div>
              <div className="label">Journals Cr</div>
              <div className="value">{totals.cr.toFixed(2)}</div>
            </div>
          </div>
          <div className="hint" style={{ marginTop: 8, opacity: 0.8 }}>
            Journals are posted with source_type <b>inventory_movement</b> and
            source_id = movement_id.
          </div>
        </div>

        <div className="fa-panel" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Lines</div>
          <table className="fa-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Unit Cost</th>
                <th>Total Cost</th>
                <th>Batch</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.inventory_items?.sku}</td>
                  <td style={{ fontWeight: 900 }}>{l.inventory_items?.name}</td>
                  <td>{Number(l.quantity || 0)}</td>
                  <td>{l.inventory_items?.unit}</td>
                  <td>
                    {l.unit_cost == null ? "—" : Number(l.unit_cost).toFixed(2)}
                  </td>
                  <td>
                    {l.total_cost == null
                      ? "—"
                      : Number(l.total_cost).toFixed(2)}
                  </td>
                  <td>{l.batch_no || "—"}</td>
                  <td>{l.expiry_date || "—"}</td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ opacity: 0.75 }}>
                    No lines found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="fa-panel" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            Journal Entries
          </div>
          <table className="fa-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Account</th>
                <th>Dr</th>
                <th>Cr</th>
                <th>Reversal</th>
              </tr>
            </thead>
            <tbody>
              {journals.map((j) => (
                <tr key={j.id}>
                  <td>{j.date}</td>
                  <td>{j.description}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {j.account_id}
                  </td>
                  <td>{Number(j.money_in || 0).toFixed(2)}</td>
                  <td>{Number(j.money_out || 0).toFixed(2)}</td>
                  <td>{j.is_reversal ? "Yes" : "No"}</td>
                </tr>
              ))}
              {journals.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ opacity: 0.75 }}>
                    No journals found (transfers won’t post journals).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
